import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, MapPin, Gavel, Calendar, Package, ArrowRight, Truck, Inbox, Route } from "lucide-react";
import { fmtDuration } from "@/lib/format";
import { StorageBadge } from "@/components/StorageBadge";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-5 flex items-center gap-4">
      <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${accent ? "bg-kinetic-light" : "bg-secondary"}`}>
        <Icon className={`h-5 w-5 ${accent ? "text-kinetic" : "text-asphalt"}`} />
      </div>
      <div>
        <p className="font-mono text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="font-display text-2xl font-bold">{value}</p>
      </div>
    </Card>
  );
}

function JobCard({ job, onClick }) {
  return (
    <Card data-testid={`job-card-${job.id}`} onClick={onClick}
      className="p-5 cursor-pointer hover:border-kinetic hover:shadow-lg transition-all group">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="font-display font-semibold text-lg leading-tight group-hover:text-kinetic transition-colors">{job.title}</h3>
        <StatusBadge status={job.status} />
      </div>
      <div className="space-y-1.5 text-sm text-muted-foreground">
        <p className="flex items-center gap-2 truncate"><MapPin className="h-4 w-4 text-emerald-500 shrink-0" /> {job.pickup_address}</p>
        <p className="flex items-center gap-2 truncate"><MapPin className="h-4 w-4 text-kinetic shrink-0" /> {job.dropoff_address}</p>
        {job.distance_km != null && <p className="flex items-center gap-2"><Route className="h-4 w-4 shrink-0 text-asphalt" /> <span className="font-medium text-asphalt">{job.distance_km} km{job.duration_min != null ? ` · ${fmtDuration(job.duration_min)}` : ""}</span> {job.distance_is_driving ? "by road" : "approx"}</p>}
        {job.preferred_date && <p className="flex items-center gap-2"><Calendar className="h-4 w-4 shrink-0" /> {job.preferred_date}</p>}
        <StorageBadge job={job} />
      </div>
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
        <div className="flex items-center gap-2">
          <Gavel className="h-4 w-4 text-kinetic" />
          <span className="font-mono text-sm font-semibold">{job.bid_count} {job.bid_count === 1 ? "bid" : "bids"}</span>
          {job.lowest_bid != null && <span className="text-sm text-muted-foreground">· from ${job.lowest_bid}</span>}
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-kinetic group-hover:translate-x-1 transition-all" />
      </div>
    </Card>
  );
}

function Empty({ onPost }) {
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-20 text-center">
      <div className="h-16 w-16 rounded-2xl bg-secondary flex items-center justify-center mb-4"><Inbox className="h-8 w-8 text-muted-foreground" /></div>
      <p className="font-display font-semibold text-lg">No jobs here yet</p>
      <p className="text-sm text-muted-foreground mb-5">Post your first move and start receiving competitive bids.</p>
      <Button onClick={onPost} data-testid="empty-post-job-btn" className="bg-kinetic hover:bg-kinetic-hover text-white gap-2"><Plus className="h-4 w-4" /> Post a job</Button>
    </div>
  );
}

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/jobs/mine").then(({ data }) => setJobs(data)).finally(() => setLoading(false));
  }, []);

  const active = jobs.filter((j) => ["open", "accepted"].includes(j.status));
  const completed = jobs.filter((j) => ["completed", "cancelled"].includes(j.status));
  const drafts = jobs.filter((j) => j.status === "draft");
  const totalBids = jobs.reduce((s, j) => s + j.bid_count, 0);

  const goto = (id) => navigate(`/jobs/${id}`);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-xs text-kinetic uppercase tracking-widest mb-1">Customer Dashboard</p>
            <h1 className="font-display text-3xl font-bold">Hi {user?.name?.split(" ")[0]} 👋</h1>
          </div>
          <Button onClick={() => navigate("/post-job")} data-testid="dashboard-post-job-btn" className="bg-kinetic hover:bg-kinetic-hover text-white gap-2"><Plus className="h-4 w-4" /> Post a Job</Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat icon={Package} label="Active jobs" value={active.length} accent />
          <Stat icon={Gavel} label="Total bids" value={totalBids} />
          <Stat icon={Truck} label="Completed" value={completed.length} />
          <Stat icon={Calendar} label="Drafts" value={drafts.length} />
        </div>

        <Tabs defaultValue="active">
          <TabsList className="mb-6">
            <TabsTrigger value="active" data-testid="tab-active-jobs">Active ({active.length})</TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed-jobs">History ({completed.length})</TabsTrigger>
            <TabsTrigger value="drafts" data-testid="tab-draft-jobs">Drafts ({drafts.length})</TabsTrigger>
          </TabsList>
          {loading ? (
            <p className="text-muted-foreground">Loading your jobs…</p>
          ) : (
            <>
              <TabsContent value="active">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {active.length ? active.map((j) => <JobCard key={j.id} job={j} onClick={() => goto(j.id)} />) : <Empty onPost={() => navigate("/post-job")} />}
                </div>
              </TabsContent>
              <TabsContent value="completed">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {completed.length ? completed.map((j) => <JobCard key={j.id} job={j} onClick={() => goto(j.id)} />) : <p className="text-muted-foreground py-10">No completed jobs yet.</p>}
                </div>
              </TabsContent>
              <TabsContent value="drafts">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {drafts.length ? drafts.map((j) => <JobCard key={j.id} job={j} onClick={() => goto(j.id)} />) : <p className="text-muted-foreground py-10">No drafts.</p>}
                </div>
              </TabsContent>
            </>
          )}
        </Tabs>
      </main>
    </div>
  );
}
